import React from 'react';
import { FaEye, FaBullseye, FaStar, FaHeart, FaLightbulb, FaChartLine, FaHandsHelping, FaGlobe, FaGraduationCap, FaBookOpen, FaArrowRight } from 'react-icons/fa';

const MissionVision = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50/30">
      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-blue-800 via-indigo-700 to-purple-800 py-24 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-6 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Mission & Vision</h1>
          <div className="w-24 h-1 bg-yellow-400 mx-auto rounded-full mb-6"></div>
          <p className="text-xl max-w-3xl mx-auto">Our guiding principles and commitment to excellence in education</p>
        </div>
      </div>

      <div className="container mx-auto px-6 py-16">
        {/* Vision & Mission Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center">
                <FaEye className="text-3xl text-white" /> {/* Changed to white */}
              </div>
              <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-3 py-1 rounded-full">Our Aspiration</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Our Vision</h2>
            <p className="text-gray-700 leading-relaxed text-lg mb-4">
              To be a globally recognized center of excellence in higher education, nurturing individuals who are 
              intellectually competent, morally upright, and socially committed to contribute meaningfully to 
              society and nation-building.
            </p>
            <div className="mt-4 pt-4 border-t border-blue-200">
              <p className="text-sm text-blue-700 italic">"Empowering minds to create a better tomorrow"</p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="w-16 h-16 bg-indigo-100 rounded-xl flex items-center justify-center">
                <FaBullseye className="text-3xl text-indigo-600" /> {/* Kept as indigo-600 for contrast */}
              </div>
              <span className="text-xs font-semibold text-indigo-600 bg-indigo-100 px-3 py-1 rounded-full">Our Commitment</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Our Mission</h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-3">
                <FaGraduationCap className="text-indigo-500 mt-0.5 flex-shrink-0" />
                <span>Provide quality education that fosters intellectual growth and academic excellence.</span>
              </li>
              <li className="flex items-start gap-3">
                <FaLightbulb className="text-indigo-500 mt-0.5 flex-shrink-0" />
                <span>Promote research, innovation, and critical thinking across all disciplines.</span>
              </li>
              <li className="flex items-start gap-3">
                <FaHeart className="text-indigo-500 mt-0.5 flex-shrink-0" />
                <span>Instill ethical values, social responsibility, and respect for diversity.</span>
              </li>
              <li className="flex items-start gap-3">
                <FaHandsHelping className="text-indigo-500 mt-0.5 flex-shrink-0" />
                <span>Prepare students for leadership roles and global careers through skill development.</span>
              </li>
              <li className="flex items-start gap-3">
                <FaGlobe className="text-indigo-500 mt-0.5 flex-shrink-0" />
                <span>Create an inclusive environment for holistic development and lifelong learning.</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Strategic Goals */}
        <div className="mb-20">
          <div className="text-center mb-10">
            <h3 className="text-3xl font-bold text-gray-800">Our Strategic Goals</h3>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-indigo-500 mx-auto rounded-full mt-3"></div>
            <p className="text-gray-600 mt-2">Key objectives guiding our journey toward excellence</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all hover:-translate-y-1 text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaChartLine className="text-2xl text-white" /> {/* Changed to white */}
              </div>
              <h4 className="text-xl font-bold text-gray-800 mb-2">Academic Excellence</h4>
              <p className="text-gray-600 text-sm">Achieve highest standards in teaching, learning, and research outcomes.</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all hover:-translate-y-1 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaHeart className="text-2xl text-green-600" />
              </div>
              <h4 className="text-xl font-bold text-gray-800 mb-2">Value Education</h4>
              <p className="text-gray-600 text-sm">Promote ethical values, character building, and social responsibility.</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all hover:-translate-y-1 text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaLightbulb className="text-2xl text-yellow-600" />
              </div>
              <h4 className="text-xl font-bold text-gray-800 mb-2">Research & Innovation</h4>
              <p className="text-gray-600 text-sm">Encourage research culture, collaboration, and innovative solutions.</p>
            </div>
          </div>
        </div>

        {/* Core Principles */}
        <div className="mb-20">
          <div className="text-center mb-10">
            <h3 className="text-3xl font-bold text-gray-800">Our Guiding Principles</h3>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-indigo-500 mx-auto rounded-full mt-3"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-blue-50 rounded-2xl p-6 text-center hover:shadow-md transition">
              <FaStar className="text-2xl text-blue-600 mx-auto mb-3" />
              <h4 className="font-bold text-gray-800">Student-Centric Approach</h4>
              <p className="text-sm text-gray-600">Every decision prioritizes student success and well-being.</p>
            </div>
            <div className="bg-indigo-50 rounded-2xl p-6 text-center hover:shadow-md transition">
              <FaGlobe className="text-2xl text-indigo-500 mx-auto mb-3" />
              <h4 className="font-bold text-gray-800">Global Perspective</h4>
              <p className="text-sm text-gray-600">Prepare students for global challenges and opportunities.</p>
            </div>
            <div className="bg-purple-50 rounded-2xl p-6 text-center hover:shadow-md transition">
              <FaBookOpen className="text-2xl text-purple-600 mx-auto mb-3" /> {/* Changed from text-blue-300 to purple-600 */}
              <h4 className="font-bold text-gray-800">Lifelong Learning</h4>
              <p className="text-sm text-gray-600">Foster curiosity and continuous skill development.</p>
            </div>
            <div className="bg-green-50 rounded-2xl p-6 text-center hover:shadow-md transition">
              <FaHandsHelping className="text-2xl text-green-500 mx-auto mb-3" />
              <h4 className="font-bold text-gray-800">Community Engagement</h4>
              <p className="text-sm text-gray-600">Contribute to society through outreach and service.</p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 md:p-10 text-center text-white shadow-xl">
          <h3 className="text-2xl md:text-3xl font-bold mb-3">Be Part of Our Vision</h3>
          <p className="text-white mb-6 max-w-2xl mx-auto"> {/* Changed from text-blue-600 to white */}
            Join us in our mission to transform education and shape future leaders.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button className="bg-white text-blue-600 px-6 py-2 rounded-full font-semibold hover:shadow-lg transition flex items-center gap-2">
              Apply Now <FaArrowRight />
            </button>
            <button className="border-2 border-white text-white px-6 py-2 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition">
              Explore Programs
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MissionVision;